import React, { useState } from "react";
import { ActivityType, ItineraryActivity } from "../models/activity.model";
import { ModalCallbacks } from "../models/activities-modal.model";
import { getActivityTypeIcon } from "../models/activity-configs.model";
import AccommodationModal from "../modals/AccommodationModal";
import ActivityModal from "../modals/ActivityModal";
import FlightModal from "../modals/FlightModal";
import TransportationModal from "../modals/TransportationModal";
import FoodDrinksModal from "../modals/FoodDrinksModal";
import EventsModal from "../modals/EventsModal";
import NotesModal from "../modals/NotesModal";
import { createCheckInOutActivities } from "@/components/utils/createCheckOutFromCheckIn";

interface UseModalCallbacksProps {
  onSave: (activity: ItineraryActivity, isEditing?: boolean, editingActivityId?: string) => void;
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
  itineraryId: string;
}

export const useModalCallbacks = ({ onSave, itineraryStartDate, itineraryEndDate, itineraryId }: UseModalCallbacksProps): ModalCallbacks => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedActivityType, setSelectedActivityType] = useState<ActivityType | null>(null);
  const [editingActivity, setEditingActivity] = useState<ItineraryActivity | null>(null);

  const openModal = (type: ActivityType, icon?: React.ReactNode, activityData?: ItineraryActivity) => {
    setSelectedActivityType(type);
    setEditingActivity(activityData || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedActivityType(null);
    setEditingActivity(null);
  };

  const handleSave = (activityData: Partial<ItineraryActivity>) => {
    const isEditing = editingActivity !== null;
    const editingActivityId = editingActivity?.id;

    onSave(activityData as ItineraryActivity, isEditing, editingActivityId);
  };

  const renderModal = (): React.ReactNode => {
    if (!selectedActivityType) return null;

    const icon = getActivityTypeIcon(selectedActivityType);

    const commonProps = {
      isOpen: isModalOpen,
      onClose: closeModal,
      onSave: handleSave,
      icon,
      itineraryStartDate,
      itineraryEndDate,
      itineraryId
    };

    console.log('start and end dates in modal callbacks:', {
      itineraryStartDate, itineraryEndDate
    });

    switch (selectedActivityType) {
      case 'accommodation':
        return React.createElement(AccommodationModal, {
          ...commonProps,
          initialData: (editingActivity?.type.startsWith('accommodation')) ? editingActivity : undefined
        });
      case 'activity':
        return React.createElement(ActivityModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'activity' ? editingActivity : undefined
        });
      case 'flight':
        return React.createElement(FlightModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'flight' ? editingActivity : undefined
        });
      case 'transportation':
        return React.createElement(TransportationModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'transportation' ? editingActivity : undefined
        });
      case 'food-drinks':
        return React.createElement(FoodDrinksModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'food-drinks' ? editingActivity : undefined
        });
      case 'events':
        return React.createElement(EventsModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'events' ? editingActivity : undefined
        });
      case 'notes':
        return React.createElement(NotesModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'notes' ? editingActivity : undefined
        });
      default:
        return null;
    }
  };

  return {
    openModal,
    closeModal,
    renderModal,
    isModalOpen,
    selectedActivityType
  };
};
